import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import { VehiclesClient } from "../web-api-client.ts";
import { useNavigate } from "react-router-dom";
import useToast from "../hooks/UseToast.js";
import Loader from "./Loader.js";
import UserContext from "../providers/UserContext.js";
import ScannerLauncher from "./ScannerLauncher.js";
import Upload, { uploadAttachment } from "./Upload.js";

const NewVehicle = ({ onComplete }) => {
  const [vehicle, setVehicle] = useState({
    makeId: null,
    modelId: null,
    year: "",
    vin: "",
    stockNumber: "",
    trim: "",
    mileage: null,
    color: "",
  });

  const [selectedMake, setSelectedMake] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);

  // ✅ NEW: hold the file until we have the created vehicle id
  const [pendingPhoto, setPendingPhoto] = useState(null);

  const navigate = useNavigate();
  const modalRef = useRef(null);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const user = useContext(UserContext);

  const toOption = (x) => ({ value: x.id, label: x.label });

  /** VIN helpers */
  const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

  const normalizeVinLoose = (raw) => {
    if (raw == null) return "";
    return raw.toString().toUpperCase().replace(/[\s-]/g, "").slice(0, 18);
  };

  const normalizeVinStrict = (raw) => {
    const cleaned = normalizeVinLoose(raw);
    if (!cleaned) return "";

    let vin = cleaned;

    if (vin.length === 18 && vin.startsWith("I") && VIN_REGEX.test(vin.slice(1))) {
      vin = vin.slice(1);
    }

    if (!VIN_REGEX.test(vin)) throw new Error("Invalid VIN format");
    return vin;
  };

  const isValidVin = (v) => VIN_REGEX.test((v || "").toString().toUpperCase());

  const vinLoose = normalizeVinLoose(vehicle.vin);
  const vinIsComplete = vinLoose.length >= 17;

  const vinIsValid = vinIsComplete
    ? (() => {
        try {
          const strict = normalizeVinStrict(vinLoose);
          return isValidVin(strict);
        } catch {
          return false;
        }
      })()
    : false;

  /** ------------- MAKES LOADER ------------- **/
  const fetchMakes = useCallback(async (inputValue) => {
    const client = new VehiclesClient();
    const term = inputValue && inputValue.trim() ? inputValue.trim() : undefined;
    const data = await client.getMakes(term);
    return data.map(toOption);
  }, []);

  const debouncedFetchMakes = useMemo(
    () =>
      debounce((input, resolve, reject) => {
        fetchMakes(input).then(resolve).catch(reject);
      }, 300),
    [fetchMakes]
  );

  const loadMakes = useCallback(
    (inputValue) => new Promise((resolve, reject) => debouncedFetchMakes(inputValue, resolve, reject)),
    [debouncedFetchMakes]
  );

  /** ------------- MODELS LOADER ------------- **/
  const fetchModels = useCallback(async (makeId, inputValue) => {
    if (!makeId) return [];
    const client = new VehiclesClient();
    const term = inputValue && inputValue.trim() ? inputValue.trim() : undefined;
    const data = await client.getModelsByMake(makeId, term);
    return data.map(toOption);
  }, []);

  const debouncedFetchModels = useMemo(
    () =>
      debounce((makeId, input, resolve, reject) => {
        fetchModels(makeId, input).then(resolve).catch(reject);
      }, 300),
    [fetchModels]
  );

  const loadModels = useCallback(
    (inputValue) =>
      new Promise((resolve, reject) => {
        debouncedFetchModels(vehicle.makeId, inputValue, resolve, reject);
      }),
    [debouncedFetchModels, vehicle.makeId]
  );

  useEffect(() => {
    return () => {
      debouncedFetchMakes.cancel();
      debouncedFetchModels.cancel();
    };
  }, [debouncedFetchMakes, debouncedFetchModels]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? checked : value;
    setVehicle((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const resetForm = () => {
    setVehicle({
      makeId: null,
      modelId: null,
      year: "",
      vin: "",
      stockNumber: "",
      trim: "",
      mileage: null,
      color: "",
    });
    setSelectedMake(null);
    setSelectedModel(null);
    setPendingPhoto(null);

    setTimeout(() => {
      modalRef.current?.scrollTo?.(0, 0);
    }, 100);
  };

  const handleSubmit = async (isAddNew) => {
    setLoading(true);
    try {
      let vinToSave = vehicle.vin;
      if (vinToSave && vinToSave.trim()) {
        try {
          vinToSave = normalizeVinStrict(vinToSave);
        } catch {
          showToast("VIN looks invalid. Fix it or clear it before saving.", "warning");
          return;
        }
      }

      const client = new VehiclesClient();
      const serverVehicle = await client.createVehicle({
        ...vehicle,
        vin: vinToSave,
        dealerId: user.dealerId,
      });

      // ✅ Upload photo after create, using the SAME attachment endpoint as Upload.js
      if (pendingPhoto) {
        try {
          await uploadAttachment({
            entityId: serverVehicle.id,
            type: "vehicle",
            file: pendingPhoto,
            name: "Vehicle Photo",
            description: "",
          });
          showToast("Vehicle created and photo uploaded!", "success");
        } catch (err) {
          console.error(err);
          showToast("Vehicle created, but photo upload failed. You can add it on the edit screen.", "warning");
        }
      } else {
        showToast("Vehicle created successfully!", "success");
      }

      if (isAddNew) {
        resetForm();
      } else {
        if (onComplete) onComplete(serverVehicle);
        else navigate(`/vehicles/${serverVehicle.id}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") event.preventDefault();
  };

  const handleDecodeVin = async (vin) => {
    const raw = vin ?? vehicle.vin;

    let strictVin = "";
    try {
      strictVin = normalizeVinStrict(raw);
    } catch {
      showToast("Please enter a valid 17-character VIN (no I, O, or Q).", "warning");
      return;
    }

    if (!isValidVin(strictVin)) {
      showToast("Please enter a valid 17-character VIN (no I, O, or Q).", "warning");
      return;
    }

    setDecoding(true);
    try {
      const client = new VehiclesClient();
      const summary = await client.decodeVinSummary(strictVin, vehicle.year || undefined);

      if (!summary) {
        showToast("No results returned from VIN decode.", "warning");
        return;
      }

      setVehicle((prev) => ({
        ...prev,
        vin: strictVin,
        year: summary.year ?? prev.year,
        trim: summary.trim ?? prev.trim,
        makeId: summary.makeId ?? prev.makeId,
        modelId: summary.modelId ?? prev.modelId,
      }));

      if (summary.makeId) {
        setSelectedMake({ value: summary.makeId, label: summary.make ?? `Make #${summary.makeId}` });
      }
      if (summary.modelId) {
        setSelectedModel({ value: summary.modelId, label: summary.model ?? `Model #${summary.modelId}` });
      }

      showToast("VIN decoded and fields populated.", "success");
    } catch (err) {
      console.error(err);
      showToast("Unable to decode VIN. Please check the VIN and try again.", "danger");
    } finally {
      setDecoding(false);
    }
  };

  return (
    <div>
      <div className="modal-body">
        <div
          className="container"
          ref={modalRef}
          onKeyDown={handleKeyDown}
          style={{ overflowY: "auto", maxHeight: "80vh" }}
        >
          <div className="row">
            <div className="mb-3">
              <label htmlFor="vin" className="form-label">Vin</label>

              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  id="vin"
                  name="vin"
                  value={vehicle.vin || ""}
                  onChange={(e) => {
                    const loose = normalizeVinLoose(e.target.value);
                    setVehicle((prev) => ({ ...prev, vin: loose }));
                  }}
                  required
                />

                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleDecodeVin(vehicle.vin)}
                  disabled={decoding || !vinIsComplete}
                  title="Decode with vPIC"
                >
                  {decoding ? "Decoding…" : "Decode VIN"}
                </button>

                <ScannerLauncher
                  onBarcodeDetected={(v) => handleDecodeVin(v)}
                  mode="button"
                  buttonClasses="ms-2"
                />
              </div>

              {vinIsComplete && (
                <div className="mt-1">
                  {vinIsValid ? (
                    <small className="text-success">✓ VIN format looks valid</small>
                  ) : (
                    <small className="text-danger">✕ VIN must be 17 characters (no I, O, or Q)</small>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ✅ Photo picker using Upload in deferred mode */}
          <div className="row">
            <div className="col-12">
              <Upload
                type="vehicle"
                entityId={null}                 // no ID yet
                name="Vehicle Photo"
                description=""
                buttonLabel={pendingPhoto ? "Replace Selected Photo" : "Select Vehicle Photo"}
                autoUpload={false}              // <-- key
                showPreview={true}
                onFileSelected={(file) => setPendingPhoto(file)}
                onComplete={() => {}}
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Make</label>
              <AsyncSelect
                classNamePrefix="rs"
                className="react-select-container"
                cacheOptions
                defaultOptions
                loadOptions={loadMakes}
                value={selectedMake}
                onChange={(opt) => {
                  setSelectedMake(opt);
                  setVehicle((prev) => ({ ...prev, makeId: opt?.value ?? null, modelId: null }));
                  setSelectedModel(null);
                }}
                placeholder="Search make…"
                isClearable
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Model</label>
              <AsyncSelect
                classNamePrefix="rs"
                className="react-select-container"
                cacheOptions
                defaultOptions={!!vehicle.makeId}
                loadOptions={loadModels}
                value={selectedModel}
                onChange={(opt) => {
                  setSelectedModel(opt);
                  setVehicle((prev) => ({ ...prev, modelId: opt?.value ?? null }));
                }}
                placeholder={vehicle.makeId ? "Search model…" : "Select a make first"}
                isDisabled={!vehicle.makeId}
                isClearable
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="year" className="form-label">Year</label>
              <input
                type="number"
                className="form-control"
                id="year"
                name="year"
                value={vehicle.year || ""}
                onChange={handleChange}
                required
              />
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="trim" className="form-label">Trim</label>
              <input
                type="text"
                className="form-control"
                id="trim"
                name="trim"
                value={vehicle.trim || ""}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="stockNumber" className="form-label">Stock Number</label>
            <input
              type="text"
              className="form-control"
              id="stockNumber"
              name="stockNumber"
              value={vehicle.stockNumber || ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="mileage" className="form-label">Mileage</label>
            <input
              type="number"
              className="form-control"
              id="mileage"
              name="mileage"
              value={vehicle.mileage || ""}
              onChange={handleChange}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="color" className="form-label">Color</label>
            <input
              type="text"
              className="form-control"
              id="color"
              name="color"
              value={vehicle.color || ""}
              onChange={handleChange}
            />
          </div>

          {loading ? (
            <div className="text-center">
              <Loader />
            </div>
          ) : (
            <div className="text-end">
              <button className="btn btn-primary me-2" onClick={() => handleSubmit(false)}>
                Save
              </button>
              <button className="btn btn-primary" onClick={() => handleSubmit(true)}>
                Save and Add New...
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewVehicle;
